import React, { useState, useCallback, useEffect, useMemo } from 'react'
import pt from 'prop-types'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'

import RowActions from './components/RowActions'
import ActionBar from './components/ActionBar'
import SortLabel from './components/SortLabel'

import { formatBytes } from './formatters'
import { useTranslation } from 'react-i18next'

const GridTable = ({
  groupTitle,
  showHeader,
  columns,
  rows,
  getRowId,
  virtualTable,
  sorting,
  defaultSorting,
  sortingColumnExtensions,
  onSearchChange,
  searchValue,
  selection,
  onSelectionChange,
  editingRowIds,
  inlineEditing,
  onCommitChanges,
  showSelectionColumn,
  selectByRowClick,
  rowActions,
  noDataMessage,
  formatters,
  actionBar,
  customFilter,
  onRowClick,
  isRowSelectable,
  scrollable,
}) => {
  const { t } = useTranslation()

  // Search / filtering state
  const [search, setSearch] = useState(actionBar ? '' : null)
  const [globalFilter, setGlobalFilter] = useState('')

  const handleSearchChange = (value) => {
    setSearch(value)
    setGlobalFilter(value)
    onSearchChange(value)
    if (customFilter) customFilter(value)
  }

  useEffect(() => {
    setSearch(searchValue)
    setGlobalFilter(searchValue || '')
  }, [searchValue])

  // Sorting state - convert DevExpress format to TanStack format
  const initialSorting = useMemo(
    () =>
      (defaultSorting || []).map((s) => ({
        id: s.columnName,
        desc: s.direction === 'desc',
      })),
    [defaultSorting],
  )
  const [sortingState, setSortingState] = useState(initialSorting)

  // Build non-sortable column set
  const nonSortableColumns = useMemo(() => {
    const set = new Set()
    if (sortingColumnExtensions) {
      sortingColumnExtensions.forEach((ext) => {
        if (ext.sortingEnabled === false) set.add(ext.columnName)
      })
    }
    return set
  }, [sortingColumnExtensions])

  // Build formatter lookup: columnName -> formatter component
  const formatterMap = useMemo(() => {
    const map = {}
    // Built-in formatters
    ;['size'].forEach((colName) => {
      map[colName] = ({ value }) => formatBytes(value)
    })
    ;['createdAt', 'updatedAt'].forEach((colName) => {
      map[colName] = 'date' // sentinel, handled below
    })
    // Custom formatters from props
    if (formatters) {
      formatters.forEach((f) => {
        const cols = f.for || []
        cols.forEach((colName) => {
          map[colName] = f.formatter
        })
      })
    }
    return map
  }, [formatters])

  // Inline editing state
  const [editingCell, setEditingCell] = useState(null) // { rowId, columnName }

  // Convert columns prop to TanStack column defs
  const tanstackColumns = useMemo(() => {
    const cols = columns.map((col) => {
      const formatter = formatterMap[col.name]

      return {
        id: col.name,
        accessorKey: col.name,
        header: col.title || col.name,
        size: col.width === 'auto' || !col.width ? undefined : typeof col.width === 'string' && col.width.endsWith('%') ? undefined : parseInt(col.width, 10),
        enableSorting: sorting && !nonSortableColumns.has(col.name),
        meta: {
          width: col.width,
          readOnly: col.readOnly,
          enum: col.enum,
        },
        cell: ({ getValue, row: tanstackRow }) => {
          const value = getValue()
          const originalRow = tanstackRow.original

          // Inline editing
          if (
            inlineEditing &&
            editingCell &&
            editingCell.rowId === getRowId(originalRow) &&
            editingCell.columnName === col.name &&
            !col.readOnly
          ) {
            return (
              <InlineEditInput
                column={col}
                value={value}
                onCommit={(newValue) => {
                  if (newValue !== value) {
                    onCommitChanges({
                      changed: {
                        [getRowId(originalRow)]: { [col.name]: newValue },
                      },
                    })
                  }
                  setEditingCell(null)
                }}
                onCancel={() => setEditingCell(null)}
              />
            )
          }

          // Custom formatter
          if (formatter === 'date') {
            return t('formatter_date2', { date: value })
          }
          if (typeof formatter === 'function') {
            const FormatterComp = formatter
            return <FormatterComp value={value} row={originalRow} />
          }

          return value
        },
      }
    })

    return cols
  }, [
    columns,
    formatterMap,
    sorting,
    nonSortableColumns,
    inlineEditing,
    editingCell,
    getRowId,
    onCommitChanges,
    t,
  ])

  // Selection state: convert array of IDs to TanStack rowSelection object
  // Only include IDs that match visible rows to avoid false indeterminate state
  const visibleRowIds = useMemo(() => {
    const set = new Set()
    rows.forEach((r) => set.add(String(getRowId(r))))
    return set
  }, [rows, getRowId])

  const rowSelection = useMemo(() => {
    if (!onSelectionChange) return {}
    const sel = {}
    ;(selection || []).forEach((id) => {
      if (visibleRowIds.has(String(id))) {
        sel[String(id)] = true
      }
    })
    return sel
  }, [selection, onSelectionChange, visibleRowIds])

  const handleRowSelectionChange = useCallback(
    (updaterOrValue) => {
      if (!onSelectionChange) return
      const newSelection =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(rowSelection)
          : updaterOrValue
      const selectedIds = Object.keys(newSelection).filter((k) => newSelection[k])
      onSelectionChange(selectedIds)
    },
    [onSelectionChange, rowSelection],
  )

  // Determine if we should use custom filtering or global filter
  const shouldUseGlobalFilter = search && !customFilter

  const table = useReactTable({
    data: rows,
    columns: tanstackColumns,
    state: {
      sorting: sorting ? sortingState : [],
      globalFilter: shouldUseGlobalFilter ? globalFilter : '',
      rowSelection,
    },
    getRowId: (row) => String(getRowId(row)),
    onSortingChange: sorting ? setSortingState : undefined,
    onGlobalFilterChange: shouldUseGlobalFilter ? setGlobalFilter : undefined,
    onRowSelectionChange: onSelectionChange ? handleRowSelectionChange : undefined,
    getCoreRowModel: getCoreRowModel(),
    ...(sorting ? { getSortedRowModel: getSortedRowModel() } : {}),
    ...(shouldUseGlobalFilter ? { getFilteredRowModel: getFilteredRowModel() } : {}),
    enableRowSelection: onSelectionChange
      ? (isRowSelectable ? (row) => isRowSelectable(row.original) : true)
      : false,
  })

  const handleCellClick = (row, columnName) => {
    if (inlineEditing) {
      const col = columns.find((c) => c.name === columnName)
      if (col && !col.readOnly) {
        setEditingCell({ rowId: getRowId(row.original), columnName })
      }
    }
  }

  const handleRowClick = (row) => {
    if (onRowClick) {
      onRowClick(row.original)
    }
    if (selectByRowClick && onSelectionChange) {
      const rowId = String(getRowId(row.original))
      const current = { ...rowSelection }
      if (current[rowId]) {
        delete current[rowId]
      } else {
        current[rowId] = true
      }
      const selectedIds = Object.keys(current).filter((k) => current[k])
      onSelectionChange(selectedIds)
    }
  }

  const isClickable = onSelectionChange || selectByRowClick || onRowClick

  return (
    <div className={cn(scrollable && 'flex flex-col min-h-0 flex-1')}>
      {actionBar && <ActionBar {...actionBar} onSearchChange={handleSearchChange} />}
      {groupTitle}
      <div
        className={cn(
          'rounded-md border border-border overflow-hidden bg-card',
          // Scrollable mode: make this box flex-fill and let the inner shadcn
          // Table wrapper (overflow-auto) flex-fill too, so only the rows scroll
          // while the sticky header stays. Flex-fill avoids %-height (h-full does
          // not resolve against a flex-item parent).
          scrollable && 'flex-1 min-h-0 flex flex-col [&>div]:flex-1 [&>div]:min-h-0',
        )}
      >
      <Table>
        {showHeader && (
          <TableHeader className={cn(scrollable && 'sticky top-0 z-20 bg-card')}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {rowActions && <TableHead className="!w-9 !p-0 text-center" />}
                {onSelectionChange && showSelectionColumn && (
                  <TableHead className="!w-9 !py-0 !pr-0 !pl-2 text-center">
                    <Checkbox
                      checked={
                        table.getIsAllRowsSelected()
                          ? true
                          : table.getIsSomeRowsSelected()
                            ? 'indeterminate'
                            : false
                      }
                      onCheckedChange={(checked) => table.toggleAllRowsSelected(!!checked)}
                    />
                  </TableHead>
                )}
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort()
                  const sortDir = header.column.getIsSorted()
                  const meta = header.column.columnDef.meta || {}
                  const widthStyle = meta.width
                    ? typeof meta.width === 'string' && meta.width.endsWith('%')
                      ? { width: meta.width }
                      : meta.width !== 'auto'
                        ? { width: meta.width }
                        : {}
                    : {}

                  return (
                    <TableHead key={header.id} style={widthStyle}>
                      {header.isPlaceholder ? null : sorting && canSort ? (
                        <SortLabel
                          column={{ title: flexRender(header.column.columnDef.header, header.getContext()) }}
                          direction={sortDir || undefined}
                          onSort={() => header.column.toggleSorting()}
                        />
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
        )}
        <TableBody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => {
              const isSelected = row.getIsSelected()
              return (
                <TableRow
                  key={row.id}
                  data-state={isSelected ? 'selected' : undefined}
                  className={cn(isClickable && 'cursor-pointer')}
                  onClick={() => handleRowClick(row)}
                >
                  {rowActions && (
                    <TableCell className="!w-9 !p-0 text-center" onClick={(e) => e.stopPropagation()}>
                      <RowActions actions={rowActions} row={row.original} />
                    </TableCell>
                  )}
                  {onSelectionChange && showSelectionColumn && (
                    <TableCell className="!w-9 !py-0 !pr-0 !pl-2 text-center" onClick={(e) => e.stopPropagation()}>
                      {row.getCanSelect() ? (
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => row.toggleSelected(!!checked)}
                        />
                      ) : null}
                    </TableCell>
                  )}
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta || {}
                    const colName = cell.column.id
                    const cellStyle = {
                      overflow: colName === 'tags' ? 'visible' : 'hidden',
                      textOverflow: colName === 'tags' ? 'initial' : 'ellipsis',
                    }

                    return (
                      <TableCell
                        key={cell.id}
                        style={cellStyle}
                        onClick={(e) => {
                          if (inlineEditing) {
                            e.stopPropagation()
                            handleCellClick(row, colName)
                          }
                        }}
                        tabIndex={inlineEditing && !meta.readOnly ? 0 : undefined}
                        onFocus={() => {
                          if (inlineEditing && !meta.readOnly) {
                            handleCellClick(row, colName)
                          }
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })
          ) : (
            <TableRow>
              <TableCell
                colSpan={
                  columns.length +
                  (rowActions ? 1 : 0) +
                  (onSelectionChange && showSelectionColumn ? 1 : 0)
                }
                className="h-24 text-center"
              >
                {t('Grid_no_data')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      </div>
      <div className="flex items-center justify-end py-2 px-1">
        <div className="text-xs text-muted-foreground">
          {table.getFilteredRowModel().rows.length} {t('Grid_row_count', 'row(s)')}
        </div>
      </div>
    </div>
  )
}

// Inline edit input component
const InlineEditInput = ({ column, value, onCommit, onCancel }) => {
  const [localValue, setLocalValue] = useState(value || '')

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      onCommit(localValue)
    } else if (e.key === 'Escape') {
      onCancel()
    }
  }

  if (column.enum) {
    return (
      <select
        className="form-control w-full"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={() => onCommit(localValue)}
        onKeyDown={handleKeyDown}
        autoFocus
      >
        {column.enum.map((i) => (
          <option key={i}>{i}</option>
        ))}
      </select>
    )
  }

  return (
    <input
      className="form-control w-full"
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={() => onCommit(localValue)}
      onKeyDown={handleKeyDown}
      autoFocus
    />
  )
}

GridTable.propTypes = {
  groupTitle: pt.oneOf([pt.string, pt.node]),
  columns: pt.array.isRequired,
  rows: pt.array.isRequired,
  getRowId: pt.func,
  virtualTable: pt.bool,
  showHeader: pt.bool,
  sorting: pt.bool,
  sortingColumnExtensions: pt.array,
  search: pt.bool,
  searchValue: pt.string,
  onSelectionChange: pt.func,
  onSearchChange: pt.func,
  selection: pt.array,
  editingRowIds: pt.array,
  selectByRowClick: pt.bool,
  scrollable: pt.bool,
  inlineEditing: pt.bool,
  onCommitChanges: pt.func,
  showSelectionColumn: pt.bool,
  noDataMessage: pt.string,
  formatters: pt.arrayOf(pt.object).isRequired,
  actionBar: pt.oneOfType([
    pt.shape({
      newButtonText: pt.string,
      onNew: pt.func,
      onDelete: pt.func,
      actionButton: pt.node,
      actionButtonRight: pt.node,
      showSearch: pt.bool,
    }),
    pt.bool,
  ]),
  rowActions: pt.arrayOf(
    pt.shape({
      title: pt.oneOfType([pt.string, pt.func]).isRequired,
      action: pt.func.isRequired,
      color: pt.string,
    }),
  ),
  customFilter: pt.func,
  onRowClick: pt.func,
  isRowSelectable: pt.func,
}
GridTable.defaultProps = {
  getRowId: (r) => r.id,
  virtualTable: false,
  search: false,
  searchValue: '',
  showHeader: true,
  sorting: true,
  defaultSorting: [],
  sortingColumnExtensions: [],
  selectByRowClick: false,
  showSelectionColumn: true,
  selection: [],
  noDataMessage: 'No data',
  formatters: [],
  onSearchChange: () => {},
  customFilter: (_searchValue) => {},
}

GridTable.displayName = 'GridTable'

export default GridTable
